import {
  Accordion,
  AccordionDetails,
  AccordionSummary,
  Card,
  IconButton,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableRow,
  Typography,
  alpha,
  keyframes,
} from "@mui/material";
import React from "react";

import { FunctionCall } from "../lib/types";
import {
  parseAndFormatRawRequest,
  parseInput,
} from "../utils/jsonParseFunctionCall";
import { TableHeadCustom } from "../components/table";
import Iconify from "../components/iconify";
import Scrollbar from "../components/scrollbar";

const TABLE_HEAD = [
  { id: "used", label: "The following was shared:", noSort: true },
  {
    id: "policy",
    label: "Privacy policy",
    noSort: true,
    align: "right",
  },
];

const FunctionCallItem = ({
  functionCall,
  mode,
  isFunctionCalling,
}: {
  functionCall: FunctionCall;
  mode: "agents" | "web-apps";
  isFunctionCalling?: boolean;
}) => {
  const filteredTableHead = TABLE_HEAD.filter(
    (item) =>
      !(
        item.id === "policy" &&
        (!functionCall.privacyPolicy || functionCall.privacyPolicy === "null")
      )
  );

  return (
    <Accordion
      className="widget-function-call-item"
      sx={{
        mb: 0,
        "& + .widget-function-call-item": {
          mt: 1,
        },
        "&.Mui-expanded": {
          my: 1,
          boxShadow: "none",
          background: "transparent",
        },
        "&:before": {
          display: "none",
        },
      }}
      // to change deprecated TransitionProps on new version of Widget
      TransitionProps={{
        unmountOnExit: true,
      }}
    >
      <AccordionSummary
        expandIcon={
          <Iconify
            sx={{
              color: "text.secondary",
            }}
            icon="ion:chevron-down"
            width={12}
            height={12}
          />
        }
        sx={{
          gap: 1,
          padding: 0,
          minHeight: "20px !important",
          justifyContent: "flex-start",
          "& .MuiAccordionSummary-content": {
            justifyContent: "flex-start",
            flexGrow: 0,
            marginY: 0,
            "&.Mui-expanded": {
              marginY: 0,
            },
          },
          "&.Mui-expanded": {
            minHeight: "20px !important",
            marginY: 0,
          },
        }}
      >
        <Stack direction="row" flexWrap="wrap" alignItems="center" gap={0.5}>
          {isFunctionCalling ? (
            <>
              <Iconify
                icon="solar:settings-bold-duotone"
                width={16}
                height={16}
                sx={{
                  color: "secondary.light",
                  animation: `${spin} 3s linear infinite`,
                  willChange: "transform",
                }}
              />
              <ShiningText text={`Using ${functionCall.name}`} />
            </>
          ) : (
            <Typography variant="caption" color="text.secondary">
              Used {functionCall.name}
            </Typography>
          )}
        </Stack>
      </AccordionSummary>
      <AccordionDetails
        sx={{
          display: "flex",
          flexDirection: "column",
          gap: 2,
          p: 1,
        }}
      >
        {functionCall.input && functionCall.input.length > 2 ? (
          <Card
            sx={{
              border: (theme) =>
                `1px solid ${alpha(theme.palette.grey[500], 0.24)}`,
            }}
          >
            <TableContainer>
              <Scrollbar>
                <Table
                  sx={{
                    minWidth: 360,
                  }}
                >
                  <TableHeadCustom headLabel={filteredTableHead} />
                  <TableBody>
                    {Object.entries(parseInput(functionCall.input) || {}).map(
                      ([key, value], index) => (
                        <TableRow key={key}>
                          <TableCell component="th" scope="row">
                            <Stack direction="row">
                              <Typography
                                variant="body2"
                                color="text.secondary"
                                sx={{ flex: "0 0 120px" }}
                              >
                                {key.replace(/_/g, " ")}{" "}
                              </Typography>
                              <Typography variant="subtitle2">
                                {value}
                              </Typography>
                            </Stack>
                          </TableCell>

                          {index === 0 &&
                            functionCall.privacyPolicy &&
                            functionCall.privacyPolicy !== "null" && (
                              <TableCell
                                align="right"
                                component="th"
                                scope="row"
                              >
                                <IconButton
                                  href={functionCall.privacyPolicy}
                                  target="_blank"
                                  rel="noopener"
                                >
                                  <Iconify icon="lucide:link" />
                                </IconButton>
                              </TableCell>
                            )}
                        </TableRow>
                      )
                    )}
                  </TableBody>
                </Table>
              </Scrollbar>
            </TableContainer>
          </Card>
        ) : null}
        {mode === "agents" && (
          <>
            {functionCall?.rawRequest &&
              functionCall?.rawRequest?.length > 2 &&
              functionCall?.rawRequest !== "null" && (
                <Accordion
                  sx={{
                    backgroundColor: (theme) =>
                      alpha(theme.palette.grey[500], 0.24),
                    borderRadius: 2,
                    "&:last-of-type": {
                      borderRadius: 2,
                    },
                  }}
                >
                  <AccordionSummary
                    expandIcon={
                      <Iconify icon="ion:chevron-down" width={16} height={12} />
                    }
                  >
                    <Typography>[debug] request</Typography>
                  </AccordionSummary>
                  <AccordionDetails
                    sx={{
                      padding: 2,
                    }}
                  >
                    <Typography
                      sx={{
                        whiteSpace: "pre-wrap",
                        overflowWrap: "anywhere",
                      }}
                    >
                      {parseAndFormatRawRequest(functionCall.rawRequest)}
                    </Typography>
                  </AccordionDetails>
                </Accordion>
              )}

            {functionCall?.rawResponse &&
              functionCall?.rawResponse?.length > 2 &&
              functionCall?.rawResponse !== "null" && (
                <Accordion
                  sx={{
                    backgroundColor: (theme) =>
                      alpha(theme.palette.grey[500], 0.24),
                    borderRadius: 2,
                    "&:last-of-type": {
                      borderRadius: 2,
                    },
                  }}
                >
                  <AccordionSummary
                    expandIcon={
                      <Iconify icon="ion:chevron-down" width={16} height={12} />
                    }
                  >
                    <Typography>[debug] response</Typography>
                  </AccordionSummary>
                  <AccordionDetails
                    sx={{
                      padding: 2,
                    }}
                  >
                    <Typography
                      sx={{
                        whiteSpace: "pre-wrap",
                        overflowWrap: "anywhere",
                      }}
                    >
                      {parseAndFormatRawRequest(functionCall.rawResponse)}
                    </Typography>
                  </AccordionDetails>
                </Accordion>
              )}
          </>
        )}
      </AccordionDetails>
    </Accordion>
  );
};

export default FunctionCallItem;

const shine = keyframes`
  from {
    -webkit-mask-position: 150%;
  }
  to {
    -webkit-mask-position: -50%;
  }
`;

const spin = keyframes`
  from {
    transform: rotate(0deg);
  }
  to {
    transform: rotate(360deg);
  }
`;

const ShiningText = ({ text }: { text: string }) => {
  return (
    <Typography
      variant="caption"
      sx={{
        display: "inline-block",
        textDecoration: "none",
        color: "text.primary",
        position: "relative",
        WebkitMaskImage:
          "linear-gradient(-75deg, rgba(0,0,0,.6) 30%, #000 50%, rgba(0,0,0,.6) 70%)",
        WebkitMaskSize: "200%",
        animation: `${shine} 2s linear infinite`,
      }}
    >
      {text}
    </Typography>
  );
};
